import {
  type ChatTransport,
  convertToModelMessages,
  createUIMessageStream,
  Output,
  streamText,
  type ModelMessage,
  type ToolSet,
  isStepCount,
  toUIMessageStream,
  type UIMessageStreamWriter,
} from 'ai';

import { dixaGateway } from './provider';
import {
  structuredResponseSchema,
  type MimUIMessage,
} from './structuredResponse';
import { toTraceparent } from './traceparent';

/** stopWhen defaults to 1,
 *  which means that it stops immediately after processing the strucuted output.
 *  Set to 10 so that we have some "budget" to call tools.
 *  See https://ai-sdk.dev/docs/reference/ai-sdk-core/stream-text#stop-when
 */
const STEP_COUNT = 10;

const appendContextToLastUserMessage = (
  modelMessages: ModelMessage[],
  context: string,
): ModelMessage[] => {
  for (let index = modelMessages.length - 1; index >= 0; index -= 1) {
    const target = modelMessages[index];
    if (target.role !== 'user') continue;

    const note = `\n\n${context}`;
    const content =
      typeof target.content === 'string'
        ? `${target.content}${note}`
        : [
            ...(target.content as Array<{ type: string }>),
            { type: 'text', text: note },
          ];

    const next = modelMessages.slice();
    next[index] = { ...target, content } as ModelMessage;
    return next;
  }

  return modelMessages;
};

const stripSubagentActivity = (messages: MimUIMessage[]): MimUIMessage[] =>
  messages.map((message) => {
    let depth = 0;
    const parts = message.parts.filter((part) => {
      if (part.type === 'data-subagent-open') {
        depth += 1;
        return false;
      }
      if (part.type === 'data-subagent-close') {
        depth = Math.max(0, depth - 1);
        return false;
      }
      return depth === 0;
    });
    return { ...message, parts };
  });

type CreateBrowserChatTransportOptions = {
  buildTools: (
    writer: UIMessageStreamWriter<MimUIMessage>,
    history: MimUIMessage[],
  ) => ToolSet;
  getMainPrompt: () => string;
  getChatContext: () => string;
  onResponseStart: (chatId: string, prompt: MimUIMessage) => void;
};

/**
 * Builds a transport that runs the AI SDK agent loop from vercel in the browser
 * using Open SSE streams, facilitated by our own open ai compatible gateway.
 */
export const createBrowserChatTransport = ({
  buildTools,
  getMainPrompt,
  getChatContext,
  onResponseStart,
}: CreateBrowserChatTransportOptions): ChatTransport<MimUIMessage> => ({
  async sendMessages({ chatId, messages, abortSignal }) {
    const prompt = messages.findLast((message) => message.role === 'user');
    let hasResponseStarted = false;
    const modelMessages = await convertToModelMessages(
      stripSubagentActivity(messages),
      { ignoreIncompleteToolCalls: true },
    );
    const chatContext = getChatContext();
    const messagesWithContext = chatContext
      ? appendContextToLastUserMessage(modelMessages, chatContext)
      : modelMessages;
    return createUIMessageStream<MimUIMessage>({
      execute: async ({ writer }) => {
        const result = streamText({
          model: dixaGateway(getMainPrompt()),
          messages: messagesWithContext,
          // Passed unstripped, so a subagent can find what it streamed before.
          tools: buildTools(writer, messages),
          abortSignal,
          headers: { traceparent: toTraceparent(chatId) },
          output: Output.object({ schema: structuredResponseSchema }),
          stopWhen: isStepCount(STEP_COUNT),
          onChunk: () => {
            if (hasResponseStarted || !prompt) return;
            hasResponseStarted = true;
            onResponseStart(chatId, prompt);
          },
        });

        writer.merge(toUIMessageStream({ stream: result.stream }));

        try {
          const output = await result.output;
          writer.write({
            type: 'data-message',
            id: 'message',
            data: output.message,
          });
          if (output.choices?.length) {
            writer.write({
              type: 'data-choices',
              id: 'choices',
              data: output.choices,
            });
          }
        } catch {
          writer.write({
            type: 'data-message',
            id: 'message',
            data: 'Sorry, I could not produce a response. Please try again.',
          });
        }
      },
    });
  },
  async reconnectToStream() {
    return null;
  },
});
